import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import { useAcceptQuoteItems } from '~/api';
import {
  itemsByPlatformProductLengthId,
  offeredBundleQuantity,
  priceLabels,
} from './quoteDisplay';
import {
  lowestPriceAmount,
  offeringQuotesByDeliveryPrice,
  quotesByTotalDeliveryPrice,
} from './quoteOrdering';
import { toastGraphQLError } from '~/utils';
import type { ProductGroup } from '~/pages/rfq-view/itemGroups';
import { quantityInBundles } from '~/pages/rfq-view/bundleQuantity';
import {
  CreateOrdersFromQuotesModal,
  type SelectedQuoteLine,
} from './CreateOrdersFromQuotesModal';
import { ItemBlock } from './ItemBlock';
import { isOfferSelectable, itemsOnSellerOffer } from './sellerOffer';
import { SellerColumnsTable } from './SellerColumnsTable';
import {
  SellerQuoteActionList,
  type RenderQuoteActions,
} from './SellerQuoteActionSlots';
import type {
  ItemBlockGroup,
  ItemsByQuoteId,
  Quote,
} from './splitAwardGridTypes';
import type { QuoteNegotiationContext } from './quoteNegotiation';
import { useSellerResponseDrafts } from './useSellerResponseDrafts';

type SplitAwardComparisonGridProps = Readonly<{
  quotes: Quote[];
  groups: ProductGroup[];
  isRequestForQuoteClosed: boolean;
  renderQuoteActions: RenderQuoteActions;
  negotiationOf: (quote: Quote) => QuoteNegotiationContext;
}>;

export function SplitAwardComparisonGrid({
  quotes,
  groups,
  isRequestForQuoteClosed,
  renderQuoteActions,
  negotiationOf,
}: SplitAwardComparisonGridProps) {
  const { t } = useTranslation('rfq-view');
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set()
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSellerColumnLayout, setIsSellerColumnLayout] =
    useState<boolean>(true);
  const { mutateAsync: acceptQuoteItems, isPending: isSubmitting } =
    useAcceptQuoteItems();
  const sellerResponses = useSellerResponseDrafts(negotiationOf);

  const itemsByQuoteId = useMemo<ItemsByQuoteId>(
    () =>
      new Map(
        quotes.map((quote) => [
          quote.id,
          itemsByPlatformProductLengthId(itemsOnSellerOffer(quote)),
        ])
      ),
    [quotes]
  );

  const columnQuotes = useMemo(
    () =>
      quotesByTotalDeliveryPrice(
        quotes,
        itemsByQuoteId,
        groups.flatMap(({ rows }) =>
          rows.flatMap(
            ({ platformProductLengthId, requestedQuantityInPieces }) =>
              requestedQuantityInPieces == null
                ? []
                : [{ platformProductLengthId, requestedQuantityInPieces }]
          )
        )
      ),
    [groups, quotes, itemsByQuoteId]
  );

  const itemBlockGroups = useMemo<ReadonlyArray<ItemBlockGroup>>(() => {
    return groups.map((group) => ({
      group,
      blocks: group.rows.map((row) => {
        const offerings = offeringQuotesByDeliveryPrice(
          quotes,
          itemsByQuoteId,
          row.platformProductLengthId
        );
        return {
          row,
          requestedBundles:
            row.requestedQuantityInPieces != null
              ? quantityInBundles(
                  row.requestedQuantityInPieces,
                  row.piecesPerBundle
                )
              : null,
          offerings,
          offeringByQuoteId: new Map(
            offerings.map(({ quote, item }) => [quote.id, item])
          ),
          lowestAmounts: {
            pickup: lowestPriceAmount(
              offerings,
              (item) => item.offeredPickupPrice
            ),
            delivery: lowestPriceAmount(
              offerings,
              (item) => item.offeredDeliveryPrice
            ),
          },
        };
      }),
    }));
  }, [groups, quotes, itemsByQuoteId]);

  const deselectQuoteItems = (quoteId: string) => {
    const quoteItemIds = [...(itemsByQuoteId.get(quoteId)?.values() ?? [])].map(
      ({ id }) => id
    );
    setSelectedItemIds((current) => {
      const next = new Set(current);
      quoteItemIds.forEach((itemId) => next.delete(itemId));
      return next;
    });
  };

  const toggleItem = (itemId: string) => {
    setSelectedItemIds((current) => {
      const next = new Set(current);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const selectedLines = useMemo<SelectedQuoteLine[]>(() => {
    const lines: SelectedQuoteLine[] = [];
    for (const { group, blocks } of itemBlockGroups) {
      for (const { row, offerings } of blocks) {
        for (const { quote, item } of offerings) {
          if (
            !selectedItemIds.has(item.id) ||
            !isOfferSelectable(quote, item, isRequestForQuoteClosed)
          ) {
            continue;
          }
          lines.push({
            quoteItemId: item.id,
            quote,
            requestForQuoteId: quote.requestForQuote.id,
            platformProductLengthId: row.platformProductLengthId,
            productName: group.productName,
            lengthDisplayValue: row.displayValue,
            quantityInBundles: offeredBundleQuantity(item, row),
            pickupPrice: item.offeredPickupPrice ?? null,
            deliveryPrice: item.offeredDeliveryPrice ?? null,
          });
        }
      }
    }
    return lines;
  }, [itemBlockGroups, selectedItemIds, isRequestForQuoteClosed]);

  const selectableSelectedItemIds = useMemo(
    () => new Set(selectedLines.map(({ quoteItemId }) => quoteItemId)),
    [selectedLines]
  );

  const selectedItemIdsByRequest = useMemo(() => {
    const itemIdsByRequestForQuoteId = new Map<string, string[]>();
    for (const line of selectedLines) {
      const alreadySelected =
        itemIdsByRequestForQuoteId.get(line.requestForQuoteId) ?? [];
      alreadySelected.push(line.quoteItemId);
      itemIdsByRequestForQuoteId.set(line.requestForQuoteId, alreadySelected);
    }
    return itemIdsByRequestForQuoteId;
  }, [selectedLines]);

  useEffect(() => {
    if (selectedLines.length === 0) setIsModalOpen(false);
  }, [selectedLines]);

  const handleConfirm = async () => {
    const orderedItemIds: string[] = [];
    let firstFailure: unknown = null;
    let failedRequestCount = 0;

    for (const quoteItemIds of selectedItemIdsByRequest.values()) {
      try {
        await acceptQuoteItems({ quoteItemIds });
        orderedItemIds.push(...quoteItemIds);
      } catch (error) {
        failedRequestCount += 1;
        firstFailure ??= error;
      }
    }

    setSelectedItemIds((current) => {
      const stillSelected = new Set(current);
      for (const quoteItemId of orderedItemIds) {
        stillSelected.delete(quoteItemId);
      }
      return stillSelected;
    });

    if (failedRequestCount === 0) {
      toast.success(t('rfq_view.orders_created'));
      setIsModalOpen(false);
      return;
    }

    if (orderedItemIds.length > 0) {
      toast.success(
        t('rfq_view.orders_created_for_some_requests', {
          created: selectedItemIdsByRequest.size - failedRequestCount,
          total: selectedItemIdsByRequest.size,
        })
      );
    }
    toastGraphQLError(firstFailure);
  };

  return (
    <div className="flex flex-col gap-6">
      <SectionPanel
        title={t('rfq_view.seller_quotes_heading')}
        bodyClassName="p-4 flex flex-col gap-6"
        aside={
          <Button
            variant="accent"
            size="sm"
            width="auto"
            className="shrink-0"
            data-testid="toggle-comparison-orientation"
            aria-pressed={isSellerColumnLayout}
            onClick={() =>
              setIsSellerColumnLayout(
                (wasSellerColumnLayout) => !wasSellerColumnLayout
              )
            }
          >
            {isSellerColumnLayout
              ? t('rfq_view.sellers_as_rows_toggle')
              : t('rfq_view.sellers_as_columns_toggle')}
          </Button>
        }
      >
        {isSellerColumnLayout ? (
          <SellerColumnsTable
            itemBlockGroups={itemBlockGroups}
            columnQuotes={columnQuotes}
            selectedItemIds={selectableSelectedItemIds}
            onToggleItem={toggleItem}
            disabled={isRequestForQuoteClosed}
            renderQuoteActions={renderQuoteActions}
            onBuyerResponded={deselectQuoteItems}
            sellerResponses={sellerResponses}
          />
        ) : (
          itemBlockGroups.map(({ group, blocks }) => {
            const { pickupPriceLabel, deliveryPriceLabel } = priceLabels(
              group.standardUnitsCode,
              t
            );
            return (
              <div key={group.productId} className="flex flex-col gap-4">
                {blocks.map((block) => (
                  <ItemBlock
                    key={block.row.platformProductLengthId}
                    group={group}
                    block={block}
                    pickupPriceLabel={pickupPriceLabel}
                    deliveryPriceLabel={deliveryPriceLabel}
                    selectedItemIds={selectableSelectedItemIds}
                    onToggleItem={toggleItem}
                    disabled={isRequestForQuoteClosed}
                  />
                ))}
              </div>
            );
          })
        )}
        {!isSellerColumnLayout && (
          <SellerQuoteActionList
            quotes={columnQuotes}
            renderQuoteActions={renderQuoteActions}
            onBuyerResponded={deselectQuoteItems}
          />
        )}
        <div className="flex justify-end">
          <Button
            variant="primary"
            width="auto"
            disabled={selectableSelectedItemIds.size === 0}
            onClick={() => setIsModalOpen(true)}
            data-testid="create-order-from-selected-quotes"
          >
            {t('rfq_view.create_order_from_selected_quotes_button')}
          </Button>
        </div>
      </SectionPanel>
      <CreateOrdersFromQuotesModal
        isOpen={isModalOpen && selectedLines.length > 0}
        onClose={() => setIsModalOpen(false)}
        onConfirm={() => void handleConfirm()}
        isSubmitting={isSubmitting}
        selectedLines={selectedLines}
      />
    </div>
  );
}
